"use client";

import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "motion/react";
import { useTranslations } from "next-intl";
import { Icon } from "@iconify/react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

const DURATION = 15;

export default function NgGame() {
    const t = useTranslations();
    const reduce = useReducedMotion();

    const [status, setStatus] = useState("idle"); // idle | playing | over
    const [score, setScore] = useState(0);
    const [timeLeft, setTimeLeft] = useState(DURATION);
    const [best, setBest] = useState(0);
    const [target, setTarget] = useState(null);
    const [bursts, setBursts] = useState([]);

    const idRef = useRef(0);
    const burstRef = useRef(0);
    const prevBestRef = useRef(0);
    const timersRef = useRef([]);

    useEffect(() => {
        const timers = timersRef.current;
        return () => timers.forEach((id) => clearTimeout(id));
    }, []);

    function spawn(currentScore) {
        const id = (idRef.current += 1);
        const size = Math.max(34, 56 - currentScore * 1.3);
        setTarget({
            id,
            x: 10 + Math.random() * 78,
            y: 16 + Math.random() * 64,
            size,
        });
    }

    function startGame() {
        prevBestRef.current = best;
        setScore(0);
        setTimeLeft(DURATION);
        setStatus("playing");
        spawn(0);
    }

    function hit() {
        if (status !== "playing" || !target) return;
        if (!reduce) {
            const bid = (burstRef.current += 1);
            setBursts((b) => [...b, { id: bid, x: target.x, y: target.y }]);
            const tm = setTimeout(
                () => setBursts((b) => b.filter((z) => z.id !== bid)),
                480,
            );
            timersRef.current.push(tm);
        }
        setScore((s) => {
            const next = s + 1;
            spawn(next);
            return next;
        });
    }

    // Décompte
    useEffect(() => {
        if (status !== "playing") return undefined;
        if (timeLeft <= 0) {
            setStatus("over");
            return undefined;
        }
        const id = setTimeout(() => setTimeLeft((tl) => tl - 1), 1000);
        return () => clearTimeout(id);
    }, [status, timeLeft]);

    // Fin de partie
    useEffect(() => {
        if (status === "over") {
            setTarget(null);
            setBest((b) => Math.max(b, score));
        }
    }, [status, score]);

    const isRecord = status === "over" && score > 0 && score > prevBestRef.current;

    return (
        <section id="angular-game" className="pt-16">
            <h2
                className="text-2xl dark:text-white font-bold font-sourceSans"
                style={{ marginBottom: "0.5em" }}
            >
                {t("Ng_game_title")}
            </h2>
            <div className="w-20 h-1 bg-primary rounded mt-3 mb-5"></div>

            <p className="text-black dark:text-lightBlue max-w-[700px] leading-7 font-geistSans mb-0">
                {t("Ng_game_intro")}
            </p>

            <div className="mt-10 relative rounded-3xl overflow-hidden bg-gradient-to-br from-bgDark-700 via-bgDark-800 to-bgDark-900 ring-1 ring-white/10">
                {/* Glows (dégradé Angular) */}
                <div className="absolute -top-24 -right-24 w-[460px] h-[460px] bg-fuchsia-500/20 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute -bottom-24 -left-20 w-[380px] h-[380px] bg-violet-500/15 rounded-full blur-3xl pointer-events-none" />

                {/* Barre de fenêtre */}
                <div className="relative flex items-center gap-2 px-5 py-3 border-b border-white/10">
                    <span className="w-3 h-3 rounded-full bg-red-400/90" />
                    <span className="w-3 h-3 rounded-full bg-yellow-400/90" />
                    <span className="w-3 h-3 rounded-full bg-green-400/90" />
                    <span className="ml-3 inline-flex items-center gap-1.5 text-[11px] font-mono text-gray-400">
                        <Icon
                            icon="logos:angular-icon"
                            className="w-3.5 h-3.5"
                            aria-hidden="true"
                        />
                        game.component.ts
                    </span>
                    <span className="ml-auto inline-flex items-center gap-1.5">
                        <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-fuchsia-400 opacity-75" />
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-fuchsia-400" />
                        </span>
                        <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-fuchsia-300">
                            {t("Ng_game_badge")}
                        </span>
                    </span>
                </div>

                <div className="relative p-5 sm:p-7">
                    {/* HUD */}
                    <div className="flex items-center justify-between gap-3 mb-3">
                        <div className="inline-flex items-center gap-1.5 rounded-full bg-white/[0.06] ring-1 ring-white/10 px-3 py-1.5 text-[12.5px] text-gray-200">
                            <SolarIcon
                                name="gamepad-bold-duotone"
                                className="w-4 h-4 text-fuchsia-300"
                            />
                            {t("Ng_game_score")}
                            <span className="font-bold text-white tabular-nums">
                                {score}
                            </span>
                        </div>
                        <div className="inline-flex items-center gap-1.5 rounded-full bg-white/[0.06] ring-1 ring-white/10 px-3 py-1.5 text-[12.5px] text-gray-200">
                            <SolarIcon
                                name="cup-star-bold-duotone"
                                className="w-4 h-4 text-amber-300"
                            />
                            {t("Ng_game_best")}
                            <span className="font-bold text-white tabular-nums">
                                {best}
                            </span>
                        </div>
                        <div
                            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12.5px] tabular-nums transition-colors ${
                                status === "playing" && timeLeft <= 5
                                    ? "bg-rose-500/15 ring-1 ring-rose-400/30 text-rose-300"
                                    : "bg-white/[0.06] ring-1 ring-white/10 text-gray-200"
                            }`}
                        >
                            <SolarIcon
                                name="clock-circle-bold-duotone"
                                className="w-4 h-4"
                            />
                            <span className="font-bold text-white">{timeLeft}s</span>
                        </div>
                    </div>

                    {/* Zone de jeu */}
                    <div
                        className="relative w-full h-64 sm:h-72 rounded-2xl overflow-hidden bg-bgDark-900/60 ring-1 ring-white/10"
                        style={{ cursor: status === "playing" ? "crosshair" : "default" }}
                    >
                        {/* Cible */}
                        <AnimatePresence>
                            {status === "playing" && target && (
                                <motion.button
                                    key={target.id}
                                    type="button"
                                    onClick={hit}
                                    aria-label={t("Ng_game_target")}
                                    initial={reduce ? false : { scale: 0, rotate: -30 }}
                                    animate={{ scale: 1, rotate: 0 }}
                                    exit={reduce ? { opacity: 0 } : { scale: 0, opacity: 0 }}
                                    transition={{
                                        type: "spring",
                                        stiffness: 420,
                                        damping: 18,
                                    }}
                                    style={{
                                        left: `${target.x}%`,
                                        top: `${target.y}%`,
                                        width: target.size,
                                        height: target.size,
                                    }}
                                    className="absolute -translate-x-1/2 -translate-y-1/2 flex items-center justify-center rounded-full bg-white/[0.04] ring-1 ring-fuchsia-400/40 shadow-lg shadow-fuchsia-500/30 active:scale-90 transition-transform"
                                >
                                    <Icon
                                        icon="logos:angular-icon"
                                        className="w-2/3 h-2/3 pointer-events-none"
                                        aria-hidden="true"
                                    />
                                </motion.button>
                            )}
                        </AnimatePresence>

                        {/* Éclats au clic */}
                        <AnimatePresence>
                            {bursts.map((b) => (
                                <motion.span
                                    key={b.id}
                                    initial={{ scale: 0.4, opacity: 0.7 }}
                                    animate={{ scale: 2.4, opacity: 0 }}
                                    exit={{ opacity: 0 }}
                                    transition={{ duration: 0.45, ease: "easeOut" }}
                                    style={{ left: `${b.x}%`, top: `${b.y}%` }}
                                    className="absolute -translate-x-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-fuchsia-400/40 pointer-events-none"
                                />
                            ))}
                        </AnimatePresence>

                        {/* Overlay idle / over */}
                        {status !== "playing" && (
                            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 bg-bgDark-900/40 backdrop-blur-[2px]">
                                {status === "idle" ? (
                                    <>
                                        <SolarIcon
                                            name="gamepad-bold-duotone"
                                            className="w-9 h-9 text-fuchsia-300 mb-2"
                                        />
                                        <h3 className="text-lg font-bold text-white mb-1">
                                            {t("Ng_game_ready_title")}
                                        </h3>
                                        <p className="text-[13px] text-gray-300 mb-4 max-w-[300px]">
                                            {t("Ng_game_ready_desc")}
                                        </p>
                                    </>
                                ) : (
                                    <>
                                        {isRecord && (
                                            <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/15 ring-1 ring-amber-400/30 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.14em] text-amber-300 mb-3">
                                                <SolarIcon
                                                    name="cup-star-bold-duotone"
                                                    className="w-3.5 h-3.5"
                                                />
                                                {t("Ng_game_record")}
                                            </span>
                                        )}
                                        <h3 className="text-lg font-bold text-white mb-1">
                                            {t("Ng_game_over_title")}
                                        </h3>
                                        <p className="text-[13px] text-gray-300 mb-4">
                                            {t("Ng_game_over_desc", { score })}
                                        </p>
                                    </>
                                )}
                                <motion.button
                                    type="button"
                                    onClick={startGame}
                                    whileHover={reduce ? undefined : { scale: 1.04 }}
                                    whileTap={reduce ? undefined : { scale: 0.96 }}
                                    className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-fuchsia-500 to-violet-500 px-5 py-2.5 text-[14px] font-semibold text-white shadow-lg shadow-fuchsia-500/20"
                                >
                                    <SolarIcon
                                        name={
                                            status === "idle"
                                                ? "play-bold"
                                                : "restart-bold-duotone"
                                        }
                                        className="w-4 h-4"
                                    />
                                    {status === "idle"
                                        ? t("Ng_game_start")
                                        : t("Ng_game_replay")}
                                </motion.button>
                            </div>
                        )}
                    </div>

                    {/* Accroche + CTA */}
                    <div className="mt-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4 rounded-2xl bg-gradient-to-r from-fuchsia-500/10 to-violet-500/10 ring-1 ring-fuchsia-400/20 px-5 py-4">
                        <p className="text-[14px] sm:text-[15px] text-white leading-snug mb-0 max-w-[460px]">
                            {t("Ng_game_footer")}
                        </p>
                    </div>
                </div>
            </div>

            <p className="mt-4 text-sm text-black/60 dark:text-lightBlue/70 max-w-[700px] font-geistSans">
                {t("Ng_game_caption")}
            </p>
        </section>
    );
}
